import { useDroppable } from "@dnd-kit/core";
import { HomeIcon } from "@heroicons/react/outline";
import React, { memo } from "react";

import { useDragDrop } from "./DragDropProvider";

import { EntityType } from "@/queries/folder-entities/types";

const getDropZoneStateClass = ({
  isActive,
  isRootZone,
}: {
  isActive: boolean;
  isRootZone: boolean;
}): string => {
  if (isActive) {
    return "scale-[1.01] border-blue-500 bg-blue-50";
  }

  if (isRootZone) {
    return "border-gray-200 bg-gray-50/50";
  }

  return "";
};

interface DroppableZoneProps {
  id: string;
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  isRootZone?: boolean;
}

const DroppableZone: React.FC<DroppableZoneProps> = memo(
  ({ id, children, className = "", disabled = false, isRootZone = false }) => {
    const { activeItem, isDragging, draggedOverFolderId } = useDragDrop();

    const { isOver, setNodeRef } = useDroppable({
      id,
      disabled,
      data: {
        type: "dropzone",
        accepts: [
          EntityType.PROMPT,
          EntityType.SNIPPET,
          EntityType.WORKFLOW,
          EntityType.FOLDER,
          EntityType.DATASET,
          EntityType.REPORT,
          EntityType.AB_TEST,
          EntityType.INPUT_VARIABLE_SET,
          EntityType.SKILL_COLLECTION,
        ],
      },
    });

    const shouldShowRootZone =
      isRootZone &&
      isDragging &&
      activeItem &&
      activeItem.folder_id !== null &&
      activeItem.folder_id !== undefined;

    const isActive = isOver && draggedOverFolderId === -1 && isRootZone;

    if (isRootZone && !shouldShowRootZone) {
      return null;
    }

    return (
      <div
        ref={setNodeRef}
        className={`
          ${className}
          ${isRootZone ? "sticky top-0 z-10 mx-8 mb-4" : ""}
          ${getDropZoneStateClass({ isActive, isRootZone })}
          ${isRootZone ? "flex items-center justify-center rounded-lg border-2 border-dashed p-4" : ""}
          transition-colors duration-200 ease-out
        `}
        data-dropzone-id={id}
      >
        {isRootZone ? (
          <div className="flex items-center gap-2 text-sm">
            <HomeIcon className={`h-5 w-5 ${isActive ? "text-blue-500" : "text-gray-400"}`} />
            <span className={isActive ? "font-medium text-blue-700" : "text-gray-500"}>
              {isActive ? "Drop to move to Home" : "Drop here to move to Home"}
            </span>
          </div>
        ) : (
          children
        )}
      </div>
    );
  },
);

DroppableZone.displayName = "DroppableZone";

export default DroppableZone;
